import { HORIZONTAL_ELLIPSIS } from '@ardoq/global-consts';
import { truncateText } from '@ardoq/graph-labeling';

// note also that wrapString may sometimes be called just to find the line
// count so actually creating the line content may be a waste of time

/** a RegExp that matches whitespace, tab, and newline. */
const ws = new RegExp('[ \\t\\n]+'); // should include all whitespace characters as defined by Ardoq

// or possibly change "measure" to work thusly
// measure(str, a, b) returns the length of the substring

export const wrapString = (
  str: string,
  width: number,
  measure: (str: string) => number,
  maxLines: number = 3
): string[] => {
  if (maxLines <= 0) {
    return [];
  }
  const output = [];

  let currentLine = '';
  let remaining = str.trimStart();
  while (remaining && output.length < maxLines) {
    const nextWhitespace = remaining.match(ws);
    const nextToken = nextWhitespace
      ? remaining.substring(0, nextWhitespace.index)
      : remaining;
    if (!nextToken) {
      // no words remain.
      output.push(currentLine);
      return output;
    }
    const currentLineProposal = currentLine
      ? `${currentLine} ${nextToken}`
      : nextToken;
    if (measure(currentLineProposal) <= width) {
      currentLine = currentLineProposal;
      if (nextWhitespace) {
        const nextWhitespaceIndex = nextWhitespace.index ?? 0;
        const [nextWhitespaceString] = nextWhitespace;
        remaining = remaining.slice(
          nextWhitespaceIndex + nextWhitespaceString.length
        );
        if (remaining.trim().length) {
          continue;
        }
      }
      output.push(currentLine);
      return output;
    }
    if (!currentLine && remaining) {
      // the next word is too long to fit on one line.
      // Use optimized truncateText to fit as many characters as possible.
      const isLastLine = maxLines <= output.length + 1;
      const lineContinuation = isLastLine ? HORIZONTAL_ELLIPSIS : '-';

      // Truncate with the appropriate line continuation character
      const truncate = truncateText(measure);
      const truncated = truncate(remaining, width, { lineContinuation });

      if (truncated) {
        currentLine = truncated;
        // Remove the line continuation to get the actual text length
        const textWithoutContinuation = truncated.endsWith(lineContinuation)
          ? truncated.slice(0, -lineContinuation.length)
          : truncated;
        remaining = remaining.slice(textWithoutContinuation.length);
      }

      // If this was the last line, we're done
      if (isLastLine) {
        output.push(currentLine);
        return output;
      }
    }
    if (!remaining || maxLines - output.length > 1) {
      output.push(currentLine);
      currentLine = '';
      continue;
    }
    // Last line: add remaining text and truncate with ellipsis
    const fullText = currentLine ? `${currentLine} ${remaining}` : remaining;
    const truncate = truncateText(measure);
    currentLine = truncate(fullText, width);
    output.push(currentLine);
    return output;
  }
  return output;
};
